import { Form, Select } from 'antd'
import React, { useEffect, useMemo, useState } from 'react'
import { UserLookupProps } from './type';
import { useSelector } from 'react-redux';
import { ProfessionalType } from '../../../../types';
import { gql, useQuery } from '@apollo/client';

const UserLookup: React.FC<UserLookupProps> = ({
  multiSelect,
  professionalOnly,
  clientOnly,
  form,
}) => {
  const professionals = useSelector((state: any) => state.professionals.professionals);
  const clients = useSelector((state: any) => state.professionals.listClients);
  const [selectedProfessional, setSelectedProfessional] = useState<string>();
  const [selectedClient, setSelectedClient] = useState<string>();
  
  // For admin users, we need to get all clients across all professionals
  // Since the professionalListClients query requires Professional auth,
  // we'll use the listAnalysesView query which admins CAN access to get client data
  const { data: analysesData, loading: loadingClients, error: clientsError } = useQuery(
    gql`
      query listAnalysesView($viewType: String!, $page: Int, $perPage: Int) {
        listAnalysesView(viewType: $viewType, page: $page, perPage: $perPage) {
          data {
            client
            clientGid
            professional
            professionalCode
          }
        }
      }
    `,
    {
      variables: {
        viewType: 'hma', // Get HMA data which includes client info
        page: 1,
        perPage: 1000,
      },
      skip: clients && clients.length > 0, // Skip if clients already in Redux (professional user)
    }
  );

  // Debug: Log when query data changes
  useEffect(() => {
    console.log('=== UserLookup Query State ===');
    console.log('Loading:', loadingClients);
    console.log('Error:', clientsError);
    console.log('AnalysesData:', analysesData);
    console.log('Skip query?', clients && clients.length > 0);
  }, [analysesData, loadingClients, clientsError, clients]);

  const optionProfessionalData = useMemo(() => {
    return professionals?.map((professional: ProfessionalType) => ({
      value: professional.id,
      label: `${professional.firstName} ${professional.familyName}`
    })) || []
  }, [professionals]);

  // Use clients from Redux state if available (for professionals), 
  // otherwise extract unique clients from analyses data (for admins)
  const optionClientData = useMemo(() => {
    let clientsList = [];
    
    if (clients && clients.length > 0) {
      // Professional user - use Redux clients
      clientsList = clients;
    } else if (analysesData?.listAnalysesView?.data) {
      // Admin user - extract unique clients from analyses data
      const analysesClients = analysesData.listAnalysesView.data;
      const uniqueClientsMap = new Map();
      
      analysesClients.forEach((item: any) => {
        const clientKey = `${item.client}_${item.clientGid}`;
        if (!uniqueClientsMap.has(clientKey) && item.client && item.clientGid) {
          uniqueClientsMap.set(clientKey, {
            id: item.clientGid, // Use clientGid as the ID
            firstName: item.client.split(' ')[0] || item.client, // First part of name
            familyName: item.client.split(' ').slice(1).join(' ') || '', // Rest of name
            fullName: item.client,
          });
        }
      });
      
      clientsList = Array.from(uniqueClientsMap.values());
    }
    
    console.log('UserLookup - clients from Redux:', clients);
    console.log('UserLookup - clients from Analyses:', analysesData?.listAnalysesView?.data);
    console.log('UserLookup - final clientsList:', clientsList);
    console.log('UserLookup - clientsList length:', clientsList.length);
    
    if (!clientsList || clientsList.length === 0) {
      console.log('UserLookup - No clients available!');
      return [];
    }
    
    const mappedClients = clientsList.map((client: any) => {
      console.log('UserLookup - Mapping client:', client);
      return {
        value: client.id,
        label: client.fullName || `${client.firstName} ${client.familyName}`.trim()
      };
    });
    
    console.log('UserLookup - Final mapped clients:', mappedClients);
    return mappedClients;
  }, [clients, analysesData]);

  const handleSelectProfessional = (newValue: string) => {
    setSelectedProfessional(newValue);
    // Reset client selection when professional changes
    setSelectedClient(undefined);
    // Reset the form field value for client
    if (form) {
      form.setFieldValue('client', undefined);
    }
  };
  
  const handleSelectClient = (newValue: string) => {
    setSelectedClient(newValue);
  };

  return (
    <>
      {!clientOnly && (
        <Form.Item
          labelAlign="left"
          name="professional"
          label="Professional"
          rules={[
            {
              required: true,
              message: 'Professional can\'t be blank',
            },
          ]}
        >
          {/* <StyledButtonInput
          onClick={() => setOpenUserModal(true)}
        >
          <span>{placeholder}</span>
        </StyledButtonInput> */}
          <Select
            mode={multiSelect ? 'multiple' : undefined}
            showSearch
            value={selectedProfessional}
            placeholder={'Find Professional ...'}
            defaultActiveFirstOption={false}
            suffixIcon={null}
            optionFilterProp="children"
            filterOption={(input: string, option: any) => option.label?.includes(input)}
            onChange={handleSelectProfessional}
            notFoundContent={null}
            options={(optionProfessionalData || []).map((d: any) => ({
              value: d.value,
              label: d.label,
            }))}
            style={{ width: '100%' }}
          />
        </Form.Item>
      )}
      {!professionalOnly && (
        <Form.Item
          labelAlign="left"
          name="client"
          label="Client"
          rules={[
            {
              required: true,
              message: 'Client can\'t be blank',
            },
          ]}
        >
          <Select
            mode={multiSelect ? 'multiple' : undefined}
            showSearch
            value={selectedClient}
            placeholder={loadingClients ? 'Loading clients...' : 'Find Client ...'}
            defaultActiveFirstOption={false}
            suffixIcon={null}
            optionFilterProp="children"
            filterOption={(input: string, option: any) => option.label?.toLowerCase().includes(input.toLowerCase())}
            onChange={handleSelectClient}
            notFoundContent={loadingClients ? 'Loading...' : (optionClientData.length === 0 ? 'No clients available' : null)}
            loading={loadingClients}
            disabled={loadingClients}
            options={optionClientData}
            style={{ width: '100%' }}
          />
        </Form.Item>
      )}
      {/* <Modal
        title={`${userType} lookup`}
        open={openUserModal}
        onOk={() => setOpenUserModal(false)}
        onCancel={() => setOpenUserModal(false)}
        footer={null}
        width={900}
      >
        <ModalContentWrapper>
          <Select
            showSearch
            value={value}
            placeholder={'Find Professional ...'}
            defaultActiveFirstOption={false}
            suffixIcon={null}
            optionFilterProp="children"
            filterOption={(input: string, option: any) => option.label?.includes(input)}
            onSearch={handleSearch}
            onChange={handleChange}
            notFoundContent={null}
            options={(optionData || []).map((d: any) => ({
              value: d.value,
              label: d.label,
            }))}
            style={{ width: '100%' }}
          />
        </ModalContentWrapper>
      </Modal> */}
    </>
  )
}

export default UserLookup